import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  DollarSign, ChevronDown, X, Sparkles, Droplets, Heart, Zap, Layers, ArrowRight,
} from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';

/**
 * A sticky floating "See Pricing" pill that, on click, opens a modal
 * offering the visitor a choice of which service pricing page to jump into.
 * Designed for pages like Home/Promotions where there isn't a single
 * pricing section to scroll to.
 *
 * The pill shows up after the visitor has scrolled slightly past the top
 * hero and can be dismissed with the X button.
 */

const SERVICE_PAGES = [
  {
    id: 'skin-boosters',
    enName: 'Skin Boosters',
    frName: 'Skin Boosters',
    enDesc: '30+ Redensity 1 treatments, combos & programs',
    frDesc: '30+ traitements Redensity 1, combos et programmes',
    href: '/skin-boosters#treatments-section',
    icon: Droplets,
    color: 'bg-teal-500',
    hover: 'hover:bg-teal-50 hover:border-teal-300',
    startFrom: 399,
  },
  {
    id: 'dermal-fillers',
    enName: 'Dermal Fillers',
    frName: 'Fillers Dermiques',
    enDesc: 'Volume, contouring & lip enhancement',
    frDesc: 'Volume, contour et rehaussement des lèvres',
    href: '/dermal-fillers#pricing',
    icon: Heart,
    color: 'bg-purple-500',
    hover: 'hover:bg-purple-50 hover:border-purple-300',
    startFrom: 599,
  },
  {
    id: 'botox',
    enName: 'Botox & Neuromodulators',
    frName: 'Botox & Neuromodulateurs',
    enDesc: 'Wrinkle relaxing — unlimited units per area',
    frDesc: 'Relaxation des rides — unités illimitées par zone',
    href: '/botox-ottawa#pricing',
    icon: Zap,
    color: 'bg-indigo-500',
    hover: 'hover:bg-indigo-50 hover:border-indigo-300',
    startFrom: 249,
  },
  {
    id: 'sculptra',
    enName: 'Sculptra',
    frName: 'Sculptra',
    enDesc: 'Collagen bio-stimulator — results up to 2 years',
    frDesc: "Bio-stimulateur de collagène — résultats jusqu'à 2 ans",
    href: '/sculptra#pricing-section',
    icon: Sparkles,
    color: 'bg-amber-500',
    hover: 'hover:bg-amber-50 hover:border-amber-300',
    startFrom: 899,
  },
  {
    id: 'microneedling',
    enName: 'Microneedling + PRP',
    frName: 'Microneedling + PRP',
    enDesc: 'Medical-grade skin renewal & PRP hair',
    frDesc: 'Renouvellement cutané médical & PRP cheveux',
    href: '/microneedling#pricing',
    icon: Layers,
    color: 'bg-rose-500',
    hover: 'hover:bg-rose-50 hover:border-rose-300',
    startFrom: 399,
  },
];

export const PricingPickerPill = ({ accent = 'amber' }) => {
  const { language } = useLanguage();
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      if (dismissed) return;
      setVisible(window.scrollY > 400);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [dismissed]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    // Lock body scroll while modal open
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open]);

  const pillText = language === 'fr' ? 'Voir les Prix' : 'See Pricing';

  return (
    <>
      {visible && !dismissed && (
        <div
          className="fixed left-1/2 -translate-x-1/2 bottom-20 md:bottom-6 z-40 animate-slide-up"
          style={{ animationDuration: '0.35s' }}
          data-testid="pricing-picker-pill-wrapper"
        >
          <div className="flex items-center gap-1.5 bg-white rounded-full shadow-2xl border-2 border-amber-400 overflow-hidden">
            <button
              type="button"
              onClick={() => setOpen(true)}
              data-testid="pricing-picker-pill"
              className="flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-stone-900 px-4 md:px-5 py-2.5 md:py-3 font-semibold text-sm md:text-base transition-transform hover:scale-[1.02]"
            >
              <DollarSign className="w-4 h-4 md:w-5 md:h-5" />
              <span>{pillText}</span>
              <ChevronDown className="w-4 h-4 md:w-5 md:h-5 animate-bounce" />
            </button>
            <button
              type="button"
              onClick={() => setDismissed(true)}
              aria-label={language === 'fr' ? 'Fermer' : 'Dismiss'}
              data-testid="pricing-picker-pill-dismiss"
              className="pr-3 pl-1 py-2 text-gray-400 hover:text-gray-700 transition-colors"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      )}

      {/* Picker Modal */}
      {open && (
        <div
          className="fixed inset-0 z-[120] flex items-end md:items-center justify-center p-0 md:p-4 bg-black/60 backdrop-blur-sm animate-fade-in"
          onClick={() => setOpen(false)}
          data-testid="pricing-picker-modal"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white w-full md:max-w-2xl rounded-t-3xl md:rounded-3xl shadow-2xl max-h-[85vh] overflow-hidden flex flex-col animate-slide-up"
          >
            {/* Header */}
            <div className="flex-shrink-0 bg-gradient-to-r from-amber-500 via-amber-400 to-amber-500 px-5 md:px-6 py-4 md:py-5 relative">
              <button
                onClick={() => setOpen(false)}
                className="absolute top-3 right-3 md:top-4 md:right-4 text-stone-900/70 hover:text-stone-900 transition-colors"
                aria-label={language === 'fr' ? 'Fermer' : 'Close'}
                data-testid="pricing-picker-modal-close"
              >
                <X className="w-5 h-5 md:w-6 md:h-6" />
              </button>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-stone-900 flex items-center justify-center flex-shrink-0">
                  <DollarSign className="w-5 h-5 md:w-6 md:h-6 text-amber-400" />
                </div>
                <div>
                  <h3 className="text-lg md:text-2xl text-stone-900 font-semibold leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
                    {language === 'fr' ? 'Quels prix voulez-vous voir?' : 'Which pricing would you like to see?'}
                  </h3>
                  <p className="text-stone-900/80 text-xs md:text-sm mt-0.5">
                    {language === 'fr'
                      ? 'Choisissez une catégorie — prix complet, forfaits et promotions.'
                      : 'Pick a category — full prices, packages & promotions.'}
                  </p>
                </div>
              </div>
            </div>

            {/* Service options */}
            <div className="flex-1 overflow-y-auto p-4 md:p-5 space-y-2.5">
              {SERVICE_PAGES.map((svc) => {
                const Icon = svc.icon;
                const name = language === 'fr' ? svc.frName : svc.enName;
                const desc = language === 'fr' ? svc.frDesc : svc.enDesc;
                return (
                  <Link
                    key={svc.id}
                    to={svc.href}
                    onClick={() => {
                      setOpen(false);
                      // ensure hash fragment triggers scroll after navigation
                      setTimeout(() => {
                        const hashPart = svc.href.split('#')[1];
                        if (hashPart) {
                          const el = document.getElementById(hashPart);
                          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }
                      }, 400);
                    }}
                    data-testid={`pricing-picker-option-${svc.id}`}
                    className={`group flex items-center justify-between gap-3 p-3 md:p-4 rounded-xl border-2 border-gray-200 bg-white ${svc.hover} transition-all`}
                  >
                    <div className="flex items-center gap-3 md:gap-4 min-w-0">
                      <div className={`flex-shrink-0 w-10 h-10 md:w-12 md:h-12 rounded-full ${svc.color} flex items-center justify-center shadow-md`}>
                        <Icon className="w-5 h-5 md:w-6 md:h-6 text-white" />
                      </div>
                      <div className="min-w-0">
                        <p className="font-semibold text-gray-900 text-sm md:text-base truncate" style={{ fontFamily: "'Playfair Display', serif" }}>
                          {name}
                        </p>
                        <p className="text-xs md:text-sm text-gray-500 truncate">{desc}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 md:gap-3 flex-shrink-0">
                      <div className="hidden sm:flex flex-col items-end">
                        <span className="text-[10px] md:text-xs text-gray-400 uppercase tracking-wider">
                          {language === 'fr' ? 'à partir de' : 'from'}
                        </span>
                        <span className="text-sm md:text-base font-bold text-gray-900">
                          ${svc.startFrom}
                        </span>
                      </div>
                      <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-gray-900 group-hover:translate-x-1 transition-all" />
                    </div>
                  </Link>
                );
              })}
            </div>

            {/* Footer */}
            <div className="flex-shrink-0 bg-gray-50 px-5 md:px-6 py-3 md:py-4 text-center border-t border-gray-100">
              <p className="text-xs text-gray-500">
                {language === 'fr'
                  ? 'Tous les prix sont en CAD, avant taxes applicables.'
                  : 'All prices are in CAD, before applicable taxes.'}
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PricingPickerPill;
